import React, { Component } from 'react';
import PropTypes from 'prop-types';
import difference from 'lodash/difference';

import { ToastContainer } from '@spotahome/ui-library';

import { localesShape } from '@spotahome/soyuz/shapes';
import { withLocales } from '@spotahome/soyuz/client';

import { isLogged } from '@common/graphql/queries/user';
import { IsUserLoggedProvider } from '@common/context/IsUserLoggedContext';
import FavoriteClaimLoginModal from '@components/FavoriteClaimLoginModal';

import HeaderContainer from '../../common/components/HeaderContainer';
import SessionExpiredModal from '../../common/components/SessionExpiredModal';
import Cookies from '../../common/components/Cookies';
import Footer from '../../common/components/Footer';

import { getFavorites } from '../../common/graphql/queries/favorites';

import { buildHeadersData } from '../conversation/builders/headers';

import getGqlClient from '../../common/graphql/client';

import Favorites from './components/Favorites';

import '../../common/scss/init.scss';

const FAVORITES_UPDATE_INTERVAL_MS = 60000;

async function getFavoritesRequest(currentLocale, graphqlClient) {
  try {
    const response = await getFavorites(graphqlClient)(currentLocale);

    return response.data.favorites.filter(fav => fav);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
    return [];
  }
}

function diffFavorites(currentFavorites, newFavorites) {
  const currentIds = currentFavorites.map(({ id }) => id);
  const newIds = newFavorites.map(({ id }) => id);
  return difference(newIds, currentIds).length > 0;
}

function hasNewFavorites(currentFavorites, newFavorites) {
  if (currentFavorites.length !== newFavorites.length) {
    return true;
  }
  return diffFavorites(currentFavorites, newFavorites);
}

class FavoritesPage extends Component {
  static NAME = 'FavoritesPage';

  static contextTypes = {
    data: PropTypes.shape({
      FavoritesPage: PropTypes.shape({
        isLogged: PropTypes.bool.isRequired
      }).isRequired
    }).isRequired
  };

  static propTypes = {
    locales: PropTypes.shape(localesShape)
  };

  static defaultProps = {
    locales: {}
  };

  state = {
    isLogged: this.context.data[FavoritesPage.NAME].isLogged,
    homecards: this.context.data[FavoritesPage.NAME].favorites
  };

  async componentDidMount() {
    this.startFavoritesUpdating(getGqlClient());
  }

  static async requestAllData(req, _, { graphqlClient, clientConfig }) {
    try {
      const responseIsLogged = await isLogged(graphqlClient)();

      const { locale } = req;
      const favorites = await getFavoritesRequest(locale, graphqlClient);

      return {
        headers: buildHeadersData({
          clientConfig,
          req
        }),
        data: {
          isLogged:
            responseIsLogged && responseIsLogged.data
              ? responseIsLogged.data.isLogged
              : false,
          favorites
        }
      };
    } catch (error) {
      console.error(error);
    }

    return {
      headers: {},
      body: {}
    };
  }

  componentWillUnmount() {
    clearInterval(this.timer);
  }

  handleFavorite = (id, isFavorite) => {
    if (!isFavorite) {
      this.setState(prevState => {
        return {
          homecards: prevState.homecards.filter(homecard => homecard.id !== id)
        };
      });
    }
  };

  handleFavoritesUpdate(favorites) {
    if (hasNewFavorites(this.state.homecards, favorites)) {
      this.setState({ homecards: favorites });
    }
  }

  startFavoritesUpdating(graphqlClient) {
    clearInterval(this.timer);
    const request = async () => {
      try {
        const favorites = await getFavoritesRequest(
          this.props.locales.current,
          graphqlClient
        );

        this.handleFavoritesUpdate(favorites);
      } catch (error) {
        console.error(error);
      }
    };
    this.timer = setInterval(request, FAVORITES_UPDATE_INTERVAL_MS);
  }

  render() {
    return (
      <div>
        <ToastContainer />
        <HeaderContainer />
        <IsUserLoggedProvider value={{ isUserLogged: this.state.isLogged }}>
          <Favorites
            isLogged={this.state.isLogged}
            homecards={this.state.homecards}
            onToggleFavorite={this.handleFavorite}
          />
          <SessionExpiredModal />
          <FavoriteClaimLoginModal />
        </IsUserLoggedProvider>
        <Cookies />
        <Footer />
      </div>
    );
  }
}

export default withLocales()(FavoritesPage);
